import React, { useEffect, useState } from 'react';
import { Building2, Users, Plus, Pencil, ShieldOff, KeyRound, Check, X } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

interface CompanyInfo {
  id: string;
  name: string;
  slug: string;
  is_active: boolean;
  created_at: string;
}

interface UserRecord {
  id: string;
  username: string;
  email?: string;
  is_active: boolean;
  created_at: string;
}

export default function CompanySettingsPage() {
  const { user, companyName } = useAuth();

  const [company, setCompany] = useState<CompanyInfo | null>(null);
  const [companyNameEdit, setCompanyNameEdit] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState('');

  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  // New user form
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [addingUser, setAddingUser] = useState(false);
  const [addError, setAddError] = useState('');

  // Edit user
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editPassword, setEditPassword] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    loadCompany();
    loadUsers();
  }, []);

  async function loadCompany() {
    try {
      const res = await api.get<CompanyInfo>('/company/me');
      setCompany(res.data);
      setCompanyNameEdit(res.data.name);
    } catch (e) {
      console.error('Failed to load company', e);
    }
  }

  async function loadUsers() {
    setLoadingUsers(true);
    try {
      const res = await api.get<UserRecord[]>('/auth/users');
      setUsers(res.data);
    } catch (e) {
      console.error('Failed to load users', e);
    } finally {
      setLoadingUsers(false);
    }
  }

  async function saveCompanyName() {
    if (!companyNameEdit.trim()) { setNameError('Name cannot be empty'); return; }
    setSavingName(true);
    setNameError('');
    try {
      const res = await api.put<CompanyInfo>('/company/me', { name: companyNameEdit.trim() });
      setCompany(res.data);
      setCompanyNameEdit(res.data.name);
    } catch (e: any) {
      setNameError(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingName(false);
    }
  }

  async function addUser() {
    if (!newUsername.trim() || !newPassword.trim()) { setAddError('Username and password are required'); return; }
    setAddingUser(true);
    setAddError('');
    try {
      await api.post('/auth/users', { username: newUsername.trim(), password: newPassword, email: newEmail.trim() || undefined });
      setNewUsername(''); setNewEmail(''); setNewPassword('');
      setShowAddUser(false);
      await loadUsers();
    } catch (e: any) {
      setAddError(e.response?.data?.detail || 'Failed to add user');
    } finally {
      setAddingUser(false);
    }
  }

  async function saveUserEdit(userId: string) {
    setSavingEdit(true);
    try {
      const payload: any = {};
      if (editPassword.trim()) payload.password = editPassword.trim();
      if (editEmail.trim()) payload.email = editEmail.trim();
      await api.put(`/auth/users/${userId}`, payload);
      setEditingUserId(null);
      setEditPassword('');
      setEditEmail('');
      await loadUsers();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to update user');
    } finally {
      setSavingEdit(false);
    }
  }

  async function toggleUserActive(userId: string, currentlyActive: boolean) {
    if (userId === user?.id && currentlyActive) {
      alert('You cannot deactivate your own account.');
      return;
    }
    try {
      await api.put(`/auth/users/${userId}`, { is_active: !currentlyActive });
      await loadUsers();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to update user');
    }
  }

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-bold text-white">Company Settings</h1>

      {/* Company Info */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 space-y-4">
        <div className="flex items-center space-x-2 mb-2">
          <Building2 size={18} className="text-blue-400" />
          <h2 className="text-lg font-semibold text-white">Company Profile</h2>
        </div>

        {company && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-slate-300">
            <div>
              <span className="text-slate-500 block">Company ID</span>
              <span className="font-mono text-xs text-slate-400">{company.id}</span>
            </div>
            <div>
              <span className="text-slate-500 block">Slug</span>
              <span>{company.slug}</span>
            </div>
            <div>
              <span className="text-slate-500 block">Status</span>
              <span className={company.is_active ? 'text-green-400' : 'text-red-400'}>
                {company.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div>
              <span className="text-slate-500 block">Created</span>
              <span>{new Date(company.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        )}

        {/* Edit name */}
        <div className="pt-2">
          <label className="block text-sm text-slate-400 mb-1">Fund / Company Name</label>
          <div className="flex space-x-2">
            <input
              className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
              value={companyNameEdit}
              onChange={e => setCompanyNameEdit(e.target.value)}
              placeholder="Your Fund Name"
            />
            <button
              onClick={saveCompanyName}
              disabled={savingName}
              className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
            >
              {savingName ? 'Saving…' : 'Save'}
            </button>
          </div>
          {nameError && <p className="text-red-400 text-xs mt-1">{nameError}</p>}
          <p className="text-slate-500 text-xs mt-1">
            This name appears in the navigation header for all users in your company.
          </p>
        </div>
      </div>

      {/* User Management */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 space-y-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <Users size={18} className="text-blue-400" />
            <h2 className="text-lg font-semibold text-white">Users</h2>
          </div>
          <button
            onClick={() => { setShowAddUser(!showAddUser); setAddError(''); }}
            className="flex items-center space-x-1 bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition"
          >
            <Plus size={14} />
            <span>Add User</span>
          </button>
        </div>

        {/* Add user form */}
        {showAddUser && (
          <div className="bg-slate-750 border border-slate-600 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-medium text-white">New User</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Username *</label>
                <input
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                  value={newUsername}
                  onChange={e => setNewUsername(e.target.value)}
                  placeholder="jane.doe"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Email</label>
                <input
                  type="email"
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  placeholder="jane@example.com"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-slate-400 mb-1">Password *</label>
                <input
                  type="password"
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Strong password"
                />
              </div>
            </div>
            {addError && <p className="text-red-400 text-xs">{addError}</p>}
            <div className="flex space-x-2">
              <button
                onClick={addUser}
                disabled={addingUser}
                className="bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
              >
                {addingUser ? 'Adding…' : 'Add User'}
              </button>
              <button
                onClick={() => { setShowAddUser(false); setAddError(''); }}
                className="bg-slate-700 hover:bg-slate-600 text-slate-300 px-4 py-2 rounded-lg text-sm transition"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Users table */}
        {loadingUsers ? (
          <p className="text-slate-400 text-sm">Loading users…</p>
        ) : (
          <div className="divide-y divide-slate-700">
            {users.map(u => (
              <div key={u.id} className="py-3">
                {editingUserId === u.id ? (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-white font-medium text-sm w-32 shrink-0">{u.username}</span>
                      <input
                        type="email"
                        className="flex-1 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-blue-500"
                        value={editEmail}
                        onChange={e => setEditEmail(e.target.value)}
                        placeholder="New email (optional)"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <KeyRound size={14} className="text-slate-400 shrink-0" />
                      <input
                        type="password"
                        className="flex-1 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-blue-500"
                        value={editPassword}
                        onChange={e => setEditPassword(e.target.value)}
                        placeholder="New password (leave blank to keep)"
                      />
                      <button
                        onClick={() => saveUserEdit(u.id)}
                        disabled={savingEdit}
                        className="text-green-400 hover:text-green-300 transition"
                        title="Save"
                      >
                        <Check size={16} />
                      </button>
                      <button
                        onClick={() => { setEditingUserId(null); setEditPassword(''); setEditEmail(''); }}
                        className="text-slate-400 hover:text-white transition"
                        title="Cancel"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <div className="flex items-center space-x-2">
                        <span className="text-white text-sm font-medium">{u.username}</span>
                        {u.id === user?.id && (
                          <span className="text-xs bg-blue-500/20 text-blue-300 px-1.5 py-0.5 rounded">you</span>
                        )}
                        <span className={`text-xs px-1.5 py-0.5 rounded ${u.is_active ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>
                          {u.is_active ? 'active' : 'inactive'}
                        </span>
                      </div>
                      {u.email && <p className="text-xs text-slate-400">{u.email}</p>}
                      <p className="text-xs text-slate-500">
                        Joined {new Date(u.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setEditingUserId(u.id);
                          setEditEmail(u.email || '');
                          setEditPassword('');
                        }}
                        className="text-slate-400 hover:text-blue-400 transition p-1"
                        title="Edit"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => toggleUserActive(u.id, u.is_active)}
                        className={`transition p-1 ${u.is_active ? 'text-slate-400 hover:text-red-400' : 'text-slate-500 hover:text-green-400'}`}
                        title={u.is_active ? 'Deactivate user' : 'Activate user'}
                      >
                        <ShieldOff size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Onboarding note */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
        <p className="text-slate-400 text-sm">
          <span className="text-slate-300 font-medium">Adding a new company tenant?</span>{' '}
          New tenants must be provisioned manually via database SQL. See{' '}
          <code className="text-blue-400 text-xs">TENANT_ONBOARDING.md</code> in the project root for instructions.
        </p>
      </div>
    </div>
  );
}
